import { useEffect, useState } from 'react';
import { Button, Divider, Select, Space } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { useForm } from 'antd/lib/form/Form';
import { DownloadOutlined } from '@ant-design/icons';

import { Checkbox, Form, FullPageSpinner, PaddedSection } from 'common/components';
import { BenefitDetails } from 'common/components/BenefitDetails';
import { isDebug } from 'config/env';
import { notificationText, showNotification } from 'common/helpers/notifications';
import {
    Benefit,
    DeliveryDate,
    EditBenefitRequest,
    FetchedBenefitPriority,
} from 'features/benefit/models/benefitModels';
import { Contract } from 'features/contract/models/contractModels';
import {
    formatBenefitChangeSuggestionsForApi,
    formatQuestionnaireAnswerForApi,
    getQuestionnaireAnswerInitialFormValues,
} from 'features/benefit/benefitHelpers';
import { QuestionnaireAnswers } from 'features/benefit/components/Questionnaire/QuestionnaireAnswers';
import { colors } from 'theme/variables';
import {
    QuestionnaireAnswer,
    QuestionnaireQuestion,
} from 'features/benefit/models/questionnaireModels';
import { Questionnaire } from 'features/benefit/components/Questionnaire/Questionnaire';
import { isFormValid } from 'common/helpers/formHelpers';
import { UploadMultiFiles } from 'common/components/deprecated/form';
import { evidencePath } from 'common/helpers/utils';
import { FileList } from 'features/check/components/FileList';
import { CustomEmpty } from 'common/components/CustomEmpty';
import { getBenefitPriorities } from 'features/benefit/selectors/benefitSelectors';
import { getUser, getUserRole, isAdmin } from 'features/user/selectors/userSelectors';
import { EditBenefitStatus } from 'features/check/components/EditBenefitStatus';

import { ApprovalStatus } from './ApprovalStatus';
import { GdprTerms } from './GdprTerms';
import { AssignedContractManagers } from './AssignedContractManagers';
import * as uploadSelectors from '../selectors/uploadSelectors';
import * as evidenceActions from '../actions/evidenceActions';
import { clearUploadData } from '../actions/uploadActions';
import {
    BenefitChangeSuggestionsFormItem,
    BenefitChangeSuggestionsFormValues,
} from './BenefitChangeSuggestionsFormItem/BenefitChangeSuggestionsFormItem';
import {
    BenefitChangeSuggestionFormItemLocation,
    BenefitChangeSuggestionFormItemValue,
    Evidence,
    EvidenceApproval,
} from '../models/uploadModels';
import {
    formatDeliveryDatesToSelectOptions,
    getBenefitChangeSuggestionsInitialFormValues,
} from '../helpers';
import moment from 'moment';

const SelectLabel = styled.h2`
    color: ${colors.primary};
`;

const DeliveryDateSelect = styled(Select)`
    width: 100%;
    border-radius: 4px;

    && .ant-select-selector {
        height: 42px;
        padding: 4px 11px;
    }
`;

const EvidenceContentWrapper = styled.div`
    width: 100%;
    padding: 1rem;
    border: 1px solid ${colors.antdBorder};
    border-radius: 4px;
    margin-top: 1.5rem;
`;

// `wrap` is required: antd's Space is `display: inline-flex` with no
// flex-wrap, so a right-aligned row of large buttons overflows off the
// LEFT edge of a phone viewport and becomes unreachable.
const ButtonsWrapper = styled(Space).attrs({ wrap: true })`
    width: 100%;
    justify-content: flex-end;
`;

const FilesWrapper = styled.div`
    width: 100%;
    padding: 1.5rem;
`;

type Props = {
    contractId: number;
    contract: Contract;
    benefitId: number;
    benefit: Benefit;
    hideSuggestions?: boolean;
};

type BenefitEvidenceFormValues = {
    agreedToGdpr?: boolean;
    questionnaireAnswer?: QuestionnaireAnswer[];
    benefitChangeSuggestions: BenefitChangeSuggestionsFormValues;
    benefitValue?: number;
    benefitLabel?: string;
};

const generateInitialFormValues = (
    benefit: Benefit,
    priorities: FetchedBenefitPriority[],
    evidence?: Evidence,
): BenefitEvidenceFormValues => ({
    agreedToGdpr: evidence?.agreedToGdpr ?? false,
    questionnaireAnswer: getQuestionnaireAnswerInitialFormValues(
        benefit?.evidenceQuestionnaire,
        evidence?.questionnaireAnswer || [],
    ),
    benefitChangeSuggestions: getBenefitChangeSuggestionsInitialFormValues(
        benefit,
        priorities,
        evidence?.benefitChangeSuggestions,
    ),
});

export const BenefitEvidence = ({
    contractId,
    contract,
    benefitId,
    benefit,
    hideSuggestions,
}: Props) => {
    const [form] = useForm<BenefitEvidenceFormValues>();
    const dispatch = useDispatch();

    const [deliveryDateId, setDeliveryDateId] = useState<number | undefined>();
    const [hasDeliveryDateChanged, setHasDeliveryDateChanged] = useState(false);
    const [isSpinnerVisible, setIsSpinnerVisible] = useState(false);
    const [evidenceQuestionnaire, setEvidenceQuestionnaire] = useState<
        QuestionnaireQuestion[] | undefined
    >(benefit?.evidenceQuestionnaire);
    const myEvidence = useSelector(uploadSelectors.getMyEvidence) || null;
    const priorities = useSelector(getBenefitPriorities);
    const fetchingStatus = useSelector(uploadSelectors.getFetchingStatus);
    const isUploading = useSelector(uploadSelectors.getUploadingStatus);
    const approvalSubmission = useSelector(uploadSelectors.getApprovalSubmission);
    const contractManagerDetails = useSelector(uploadSelectors.getContractManagerDetails);
    const isUserAdmin = useSelector(isAdmin);
    const user = useSelector(getUser);

    useEffect(() => {
        if (['inProgress', 'idle'].includes(fetchingStatus) && !hasDeliveryDateChanged) {
            return setIsSpinnerVisible(true);
        }
        setIsSpinnerVisible(false);
    }, [fetchingStatus]);

    useEffect(() => {
        const initialDeliveryDateId = (
            benefit.deliveryDates.find((date) => date.needsUserAction) || benefit.deliveryDates[0]
        ).id;

        if (initialDeliveryDateId) {
            setDeliveryDateId(getDefaultSelectedDeliveryDate() || initialDeliveryDateId);
        }
    }, []);

    useEffect(() => {
        if (deliveryDateId) {
            form.resetFields();
            dispatch(
                evidenceActions.getMyEvidenceRequest({
                    contractId,
                    benefitId,
                    deliveryDateId,
                    organisationId: contract.organisation,
                }),
            );
        }
    }, [deliveryDateId]);

    useEffect(() => {
        if (myEvidence) {
            form.setFieldsValue({
                ...generateInitialFormValues(benefit, priorities, myEvidence),
            });
        } else {
            form.resetFields();
        }
        if (benefit?.evidenceQuestionnaire) {
            setEvidenceQuestionnaire(benefit.evidenceQuestionnaire);
        }
    }, [myEvidence, benefit]);

    const changeDeliveryDate = (newDeliveryDateId: number) => {
        if (contract) {
            setDeliveryDateId(newDeliveryDateId);
            setHasDeliveryDateChanged(true);
            evidenceActions.getMyEvidenceRequest({
                contractId,
                benefitId,
                deliveryDateId: newDeliveryDateId,
                organisationId: contract.organisation,
            });
        }
    };

    const getDefaultSelectedDeliveryDate = () => {
        if (!benefit?.deliveryDates?.length) {
            return null;
        }
        if (myEvidence?.approved) {
            return benefit.deliveryDates.find((date) => date.isFinal)?.id;
        }
        if (benefit.deliveryDates.length === 1) {
            return benefit.deliveryDates[0].id;
        }
        return benefit.deliveryDates
            .filter((date) => !date.isFinal)
            .sort((a, b) => (moment(b.date).isBefore(moment(a.date)) ? -1 : 1))
            .shift()?.id;
    };

    const handleSubmit = (submitAction: any, selfAssessment?: boolean) => () => {
        if (!isFormValid(form)) {
            showNotification({ text: notificationText.Error });
            return;
        }

        const assessmentFlag = selfAssessment ?? false;

        dispatch(
            submitAction({
                benefitId,
                contractId: contract.id,
                deliveryDateId,
                evidenceId: myEvidence?.id,
                organisationId: contract.organisation,
                ...form.getFieldsValue(),
                questionnaireAnswer: formatQuestionnaireAnswerForApi(
                    benefit.evidenceQuestionnaire,
                    form.getFieldsValue().questionnaireAnswer,
                ),
                benefitChangeSuggestions: formatBenefitChangeSuggestionsForApi(
                    generateInitialFormValues(benefit, priorities, myEvidence)
                        ?.benefitChangeSuggestions,
                    form.getFieldsValue().benefitChangeSuggestions,
                ),
                selfAssessment: assessmentFlag,
            }),
        );

        /* Approval used to be dispatched here right after create/update,
           but that double-fired for existing records and crashed for new
           ones (no server id yet). The uploadEpic now chains the approve
           action when selfAssessment is true, using the server-returned id. */
    };

    const handleApproveEvidence = (evidence: EvidenceApproval) => {
        if (!evidence) {
            return;
        }
        dispatch(evidenceActions.approveEvidenceRequest({ ...evidence, evidenceId: evidence.id }));
    };
    const deleteFile = (fileId: number) => {
        if (deliveryDateId) {
            dispatch(
                evidenceActions.deleteEvidenceFileRequest({
                    benefitId,
                    contractId,
                    evidenceId: myEvidence?.id,
                    deliveryDateId,
                    organisationId: contract.organisation,
                    fileId,
                }),
            );
        }
    };

    const isEditBlocked =
        myEvidence?.approved &&
        ![myEvidence?.author, myEvidence?.approvedBy].every(
            (fullName) => fullName === `${user.firstName} ${user.lastName}`,
        );

    return (
        <>
            <PaddedSection grayBg bottomBorder>
                <BenefitDetails
                    key={benefit.id}
                    benefit={benefit}
                    contract={contract}
                    showFinalDeliveryDate={false}
                />
                <AssignedContractManagers contractManagerDetails={contractManagerDetails} />
                {myEvidence && benefit.selfAssessmentAllowed && (
                    <EditBenefitStatus
                        key="editBenefitStatus"
                        benefit={benefit}
                        contract={contract}
                    />
                )}
            </PaddedSection>
            {isSpinnerVisible ? (
                <FullPageSpinner />
            ) : (
                <PaddedSection>
                    <SelectLabel>Choose Date for Evidence</SelectLabel>
                    <DeliveryDateSelect defaultValue={deliveryDateId} onChange={changeDeliveryDate}>
                        {formatDeliveryDatesToSelectOptions(benefit.deliveryDates)}
                    </DeliveryDateSelect>
                    <EvidenceContentWrapper>
                        <Form
                            form={form}
                            initialValues={generateInitialFormValues(
                                benefit,
                                priorities,
                                myEvidence,
                            )}
                            hideSubmitButton
                        >
                            <ApprovalStatus
                                approved={myEvidence?.approved}
                                declined={myEvidence?.declined}
                                approvedAt={myEvidence?.approvedAt}
                                approvedBy={myEvidence?.approvedBy}
                                declinedBy={myEvidence?.declinedBy}
                                declineMessage={myEvidence?.declineMessage}
                                bottomMargin
                            />
                            {isEditBlocked ? (
                                <QuestionnaireAnswers
                                    questions={myEvidence.evidenceQuestionnaire}
                                    answers={myEvidence.questionnaireAnswer}
                                />
                            ) : (
                                <>
                                    {evidenceQuestionnaire && (
                                        <Questionnaire questions={evidenceQuestionnaire} />
                                    )}
                                    {!myEvidence?.approved && (
                                        <BenefitChangeSuggestionsFormItem
                                            evidenceDateId={deliveryDateId}
                                            benefitId={benefitId}
                                            contractId={contractId}
                                            benefitSharedUrl={benefit.sharedBenefit}
                                            isUserAdmin={isUserAdmin}
                                            benefitChangeSuggestions={{
                                                label: benefit.label,
                                                value: Number(benefit.value),
                                                location: benefit?.location,
                                            }}
                                        />
                                    )}

                                    <Checkbox name="agreedToGdpr">
                                        <GdprTerms />
                                    </Checkbox>
                                    <ButtonsWrapper>
                                        {benefit.evidenceFileTemplate && (
                                            <Button
                                                icon={<DownloadOutlined />}
                                                href={
                                                    isDebug()
                                                        ? benefit.evidenceFileTemplate
                                                        : `https://${benefit.evidenceFileTemplate}`
                                                }
                                                target="blank"
                                                rel="noopener noreferrer"
                                            >
                                                Download Evidence Template
                                            </Button>
                                        )}
                                        <Button
                                            type="primary"
                                            onClick={handleSubmit(
                                                myEvidence
                                                    ? evidenceActions.updateEvidenceRequest
                                                    : evidenceActions.createEvidenceRequest,
                                            )}
                                            size="large"
                                            disabled={isUploading || !deliveryDateId}
                                            loading={isUploading}
                                        >
                                            {`${myEvidence ? 'Update' : 'Create'} Evidence`}
                                        </Button>
                                        {isUserAdmin && (
                                            <Button
                                                type="primary"
                                                onClick={handleSubmit(
                                                    myEvidence
                                                        ? evidenceActions.updateEvidenceRequest
                                                        : evidenceActions.createEvidenceRequest,
                                                    true,
                                                )}
                                                size="large"
                                                disabled={isUploading || !deliveryDateId}
                                                loading={isUploading}
                                            >
                                                {`${
                                                    myEvidence ? 'Approve' : 'Create & Approve'
                                                } Evidence`}
                                            </Button>
                                        )}
                                        <Button
                                            type="primary"
                                            onClick={handleSubmit(
                                                evidenceActions.submitForApprovalRequest,
                                            )}
                                            size="large"
                                            disabled={
                                                isUploading ||
                                                approvalSubmission !== 'idle' ||
                                                myEvidence?.approved ||
                                                !myEvidence?.questionnaireAnswer
                                            }
                                            loading={approvalSubmission === 'inProgress'}
                                        >
                                            {approvalSubmission === 'submitted'
                                                ? 'Submitted for Approval'
                                                : 'Submit for Approval'}
                                        </Button>
                                    </ButtonsWrapper>
                                </>
                            )}
                        </Form>
                        <Divider />
                        <FilesWrapper>
                            {myEvidence && deliveryDateId && !isEditBlocked ? (
                                <UploadMultiFiles
                                    uploadUrl={`${evidencePath({
                                        organisationId: benefit.organisation,
                                        contractId: benefit.contract,
                                        benefitId: benefit.id,
                                        deliveryDateId,
                                    })}${myEvidence.id}/files/`}
                                    successAction={evidenceActions.uploadEvidenceFileSuccess}
                                    failureAction={evidenceActions.uploadEvidenceFileFailure}
                                />
                            ) : (
                                !myEvidence?.approved && (
                                    <CustomEmpty description="Please create Evidence prior to file upload." />
                                )
                            )}
                            {Boolean(myEvidence?.files?.length) && (
                                <FileList
                                    loading={isUploading}
                                    files={myEvidence?.files}
                                    onDelete={!myEvidence?.approved && deleteFile}
                                />
                            )}
                        </FilesWrapper>
                    </EvidenceContentWrapper>
                </PaddedSection>
            )}
        </>
    );
};
